import type { BoardCardRow, ProjectBoardResponse } from '@pulse/contracts';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { DayControl, NotRecorded, useBusinessDate } from '@/components/pulse/day';
import { BackIcon, ExternalIcon } from '@/components/pulse/icons';
import { NotBuiltYet, PageHeader } from '@/components/pulse/page-header';
import { SourceLink } from '@/components/pulse/source-link';
import { useProjectBoard } from '@/domains/projects/api';
import { ProjectTabs } from '@/domains/projects/project-tab';

/** The prototype's CHIP colours for a card's state. */
const CHIP: Readonly<Record<string, string>> = {
  'Needs decision': 'c-warn',
  Merged: 'c-merged',
  Approved: 'c-approved',
  'In review': 'c-review',
  'Changes requested': 'c-changes',
  'No reviewer': 'c-warn',
  Open: 'c-review',
  Completed: 'c-merged',
};
const chipOf = (state: string) => CHIP[state] ?? (state.startsWith('Held for') ? 'c-warn' : 'c-kind');

/** Projects › Board (PRJ-1 to PRJ-4), built from the prototype's `viewProject()`. A day page. */
export function ProjectBoardPage(): React.ReactElement {
  const { productSlug = '' } = useParams();
  const { date, today } = useBusinessDate();
  const page = useProjectBoard(productSlug, date, today);
  const data = page.data;
  return (
    <>
      <PageHeader
        eyebrow={`Project · ${data?.dateLabel ?? ''}`}
        title={data?.product?.name ?? 'Projects'}
        sub={data?.product?.sub ?? ''}
        right={<DayControl date={date} today={today} />}
      />
      {page.isError ? <span className="sub">There is no board for that project.</span> : null}
      {data !== undefined ? <ProjectBoardView data={data} /> : null}
    </>
  );
}

function BoardCard({ card }: { readonly card: BoardCardRow }): React.ReactElement {
  return (
    <article className="card kcard">
      <div className="khead">
        <span className="mono sid" style={{ fontSize: 12, fontWeight: 500 }}>
          {card.sid ?? ''}
        </span>
        <SourceLink className={`src ${card.source === 'GitHub' ? 'gh' : 'tw'}`} href={card.url}>
          {card.source} · {card.sourceLabel} <ExternalIcon />
        </SourceLink>
      </div>
      <SourceLink className="ktitle" href={card.url}>
        {card.title}
      </SourceLink>
      <div className="kfoot">
        <span className="row" style={{ gap: 8, fontSize: 12, color: 'var(--muted)' }}>
          <span>{card.who}</span>
          {card.pullRequest !== null ? (
            <SourceLink className="mono" href={card.pullRequest.url} style={{ textDecoration: 'none' }}>
              {card.pullRequest.label}
            </SourceLink>
          ) : null}
        </span>
        <span className={`chip ${chipOf(card.state)}`}>{card.state}</span>
      </div>
    </article>
  );
}

export function ProjectBoardView({ data }: { readonly data: ProjectBoardResponse }): React.ReactElement {
  const board = React.useRef<HTMLDivElement>(null);
  const scroll = (direction: -1 | 1) => {
    const element = board.current;
    if (element === null) return;
    const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.scrollBy({ left: direction * Math.max(280, element.clientWidth * 0.8), behavior: reduced ? 'auto' : 'smooth' });
  };
  if (data.product === null) {
    return <NotBuiltYet story="PRJ-001" what={data.recorded ? 'A project board' : 'The boards, until the first collection run,'} />;
  }
  const cards = data.columns.reduce((total, column) => total + column.cards.length, 0);
  return (
    <>
      <ProjectTabs slug={data.product.slug} hasBoard hasStories={data.product.hasStories} view="board" />
      <div className="board-wrap">
        <div className="board-tools">
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>
            {data.columns.length} stages · {cards} cards. Scroll sideways for more stages.
          </span>
          <div className="row" style={{ gap: 6 }}>
            <button type="button" className="btn" aria-label="Scroll the board left" onClick={() => scroll(-1)}>
              <BackIcon />
            </button>
            <button type="button" className="btn" aria-label="Scroll the board right" onClick={() => scroll(1)}>
              <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
                <BackIcon />
              </span>
            </button>
          </div>
        </div>
        {!data.recorded ? (
          <NotRecorded />
        ) : (
          // A scrolling region takes focus so the keyboard can scroll it (PRJ-001-AC-3, WCAG 2.1.1).
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          <div className="board" ref={board} tabIndex={0} role="region" aria-label={`${data.product.name} board`}>
            {data.columns.map((column) => (
              <section key={column.title} className="lane" aria-label={column.title}>
                <div className="lane-head">
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <h3>{column.title}</h3>
                    <span className="num" style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)' }}>
                      {column.cards.length}
                    </span>
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>{column.hint}</span>
                </div>
                {column.cards.length === 0 ? <div className="lane-empty">Nothing here today</div> : column.cards.map((card) => <BoardCard key={`${card.sourceLabel}${card.title}`} card={card} />)}
              </section>
            ))}
          </div>
        )}
        <aside className="board-side">
          <section className="card pad stack" style={{ gap: 14 }}>
            <h2>Who worked on it</h2>
            {data.contributions.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>Nobody worked on it this day.</span> : null}
            {data.contributions.map((entry) => (
              <div key={entry.userId} className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                <span className="avatar sm" style={{ background: entry.avatarColour }}>
                  {entry.initials}
                </span>
                <div className="stack" style={{ gap: 2 }}>
                  <span style={{ fontWeight: 600 }}>{entry.name}</span>
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>{entry.line}</span>
                </div>
              </div>
            ))}
          </section>
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>Where cards link</h2>
            <span style={{ color: 'var(--ink-2)' }}>{data.linkNote}</span>
          </section>
        </aside>
      </div>
    </>
  );
}
